// Unified visual builder for a profile's search terms.
//
// Keywords and keyword expressions are edited as ONE list of cards. Cards are
// OR-combined: each card can produce matches on its own. Within a card, terms
// are grouped into AND-combined conditions, each condition an OR-list of term
// chips — no knowledge of the server's expression syntax required.
//
// Storage stays split for compatibility (see tenderExpressionModel.ts):
// single-condition cards are persisted as `keywords`, AND/raw cards as
// `keywordExpressions`. Expressions that don't fit the AND-of-OR shape fall
// back to a raw-text row.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, X } from "lucide-react";

import {
  ExpressionGroups,
  ExpressionModel,
  fieldsToModels,
  modelsToFields,
  SearchTermFields,
  splitTermInput,
} from "./tenderExpressionModel";
import FieldHelpButton from "./FieldHelpButton";

interface Properties {
  keywords: string[];
  expressions: string[];
  onChange: (fields: SearchTermFields) => void;
  onHelp?: () => void;
}

const chipClass =
  "inline-flex items-center gap-1 rounded bg-secondary px-2 py-0.5 text-sm text-secondary-foreground";
const iconButtonClass =
  "inline-flex items-center justify-center rounded p-0.5 text-muted-foreground hover:text-foreground cursor-pointer";

const syncKey = (keywords: string[], expressions: string[]) =>
  JSON.stringify([keywords, expressions]);

export default function TenderExpressionBuilder(properties: Properties) {
  const { t } = useTranslation();

  // Semi-controlled: local models carry in-progress state (empty groups,
  // pending drafts) that the serialized fields cannot represent. Re-derive
  // from props only when the value changes externally (e.g. AI suggestions).
  const [models, setModels] = useState<ExpressionModel[]>(() =>
    fieldsToModels({ keywords: properties.keywords, keywordExpressions: properties.expressions })
  );
  const [syncedValue, setSyncedValue] = useState<string>(
    syncKey(properties.keywords, properties.expressions)
  );
  const incoming = syncKey(properties.keywords, properties.expressions);
  if (incoming !== syncedValue) {
    setSyncedValue(incoming);
    setModels(
      fieldsToModels({ keywords: properties.keywords, keywordExpressions: properties.expressions })
    );
  }

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [andHints, setAndHints] = useState<Record<string, boolean>>({});

  const update = (next: ExpressionModel[]) => {
    setModels(next);
    const fields = modelsToFields(next);
    setSyncedValue(syncKey(fields.keywords, fields.keywordExpressions));
    properties.onChange(fields);
  };

  const updateGroups = (cardIndex: number, groups: ExpressionGroups) => {
    update(models.map((model, index) => (index === cardIndex ? { kind: "groups", groups } : model)));
  };

  const commitDraft = (cardIndex: number, groupIndex: number) => {
    const key = `${cardIndex}:${groupIndex}`;
    const draft = drafts[key] || "";
    if (!draft.trim()) return;
    const { terms, containsAnd } = splitTermInput(draft);
    if (containsAnd) {
      setAndHints({ ...andHints, [key]: true });
      return;
    }
    if (!terms.length) return;
    const model = models[cardIndex];
    if (model.kind !== "groups") return;
    const groups = model.groups.map((group, index) =>
      index === groupIndex ? [...group, ...terms] : group
    );
    setDrafts({ ...drafts, [key]: "" });
    setAndHints({ ...andHints, [key]: false });
    updateGroups(cardIndex, groups);
  };

  const removeTerm = (cardIndex: number, groupIndex: number, termIndex: number) => {
    const model = models[cardIndex];
    if (model.kind !== "groups") return;
    let groups = model.groups.map((group, index) =>
      index === groupIndex ? group.filter((_, i) => i !== termIndex) : group
    );
    if (groups.length > 1) groups = groups.filter((group) => group.length > 0);
    if (!groups.length) groups = [[]];
    updateGroups(cardIndex, groups);
  };

  const renderGroupsCard = (model: { kind: "groups"; groups: ExpressionGroups }, cardIndex: number) => (
    <div className="flex flex-col gap-2">
      {model.groups.map((group, groupIndex) => {
        const key = `${cardIndex}:${groupIndex}`;
        return (
          <div key={groupIndex} className="flex flex-col gap-1">
            {groupIndex > 0 && (
              <div className="flex items-center gap-2">
                <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-semibold text-muted-foreground">
                  {t("tenderSearchProfiles.builder.and")}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              {group.map((term, termIndex) => (
                <span key={termIndex} className="inline-flex items-center gap-1.5">
                  {termIndex > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {t("tenderSearchProfiles.builder.or")}
                    </span>
                  )}
                  <span
                    className={chipClass}
                    title={
                      term.includes(" ")
                        ? t("tenderSearchProfiles.builder.phraseTooltip")
                        : undefined
                    }
                  >
                    {term.includes(" ") ? `„${term}“` : term}
                    <button
                      type="button"
                      className={iconButtonClass}
                      aria-label={t("tenderSearchProfiles.builder.removeTerm")}
                      onClick={() => removeTerm(cardIndex, groupIndex, termIndex)}
                    >
                      <X size={12} />
                    </button>
                  </span>
                </span>
              ))}
              <input
                className="h-7 w-44 rounded border border-input bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground focus:border-ring"
                placeholder={t("tenderSearchProfiles.builder.termPlaceholder")}
                value={drafts[key] || ""}
                onChange={(event) => {
                  setDrafts({ ...drafts, [key]: event.target.value });
                  if (andHints[key]) setAndHints({ ...andHints, [key]: false });
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === ",") {
                    event.preventDefault();
                    commitDraft(cardIndex, groupIndex);
                  }
                }}
                onBlur={() => commitDraft(cardIndex, groupIndex)}
              />
            </div>
            {andHints[key] && (
              <p className="text-xs text-destructive">
                {t("tenderSearchProfiles.builder.andHint")}
              </p>
            )}
          </div>
        );
      })}
      <div>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer"
          onClick={() => updateGroups(cardIndex, [...model.groups, []])}
        >
          <Plus size={14} />
          {t("tenderSearchProfiles.builder.addCondition")}
        </button>
      </div>
    </div>
  );

  const renderRawCard = (model: { kind: "raw"; text: string }, cardIndex: number) => (
    <div className="flex flex-col gap-1">
      <input
        className="h-8 rounded border border-input bg-transparent px-2 text-sm outline-none focus:border-ring"
        value={model.text}
        onChange={(event) =>
          update(
            models.map((item, index) =>
              index === cardIndex ? { kind: "raw", text: event.target.value } : item
            )
          )
        }
      />
      <p className="text-xs text-muted-foreground">
        {t("tenderSearchProfiles.builder.rawHint")}
      </p>
    </div>
  );

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <label className="text-sm font-medium leading-none">
          {t("tenderSearchProfiles.searchTerms")}
        </label>
        {properties.onHelp && <FieldHelpButton onClick={properties.onHelp} />}
      </div>
      <p className="text-xs text-muted-foreground">
        {t("tenderSearchProfiles.builder.hint")}
      </p>
      <div className="flex flex-col gap-2">
        {models.map((model, cardIndex) => (
          <div key={cardIndex} className="flex flex-col gap-2">
            {cardIndex > 0 && (
              <div className="flex items-center gap-2 px-1">
                <div className="h-px w-4 bg-border" />
                <span className="text-xs font-semibold text-muted-foreground">
                  {t("tenderSearchProfiles.builder.cardOr")}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
            )}
            <div className="relative rounded-md border border-input p-3 pr-9">
              <button
                type="button"
                className={`${iconButtonClass} absolute right-2 top-2`}
                aria-label={t("tenderSearchProfiles.builder.removeExpression")}
                title={t("tenderSearchProfiles.builder.removeExpression")}
                onClick={() => update(models.filter((_, index) => index !== cardIndex))}
              >
                <Trash2 size={14} />
              </button>
              {model.kind === "groups"
                ? renderGroupsCard(model, cardIndex)
                : renderRawCard(model, cardIndex)}
            </div>
          </div>
        ))}
        <div>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium hover:text-foreground cursor-pointer"
            onClick={() => update([...models, { kind: "groups", groups: [[]] }])}
          >
            <Plus size={14} />
            {t("tenderSearchProfiles.builder.addExpression")}
          </button>
        </div>
      </div>
    </div>
  );
}
